'use client'

import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'

/**
 * Ziele, vor denen die schwebenden Aktionen mobil Platz machen: das
 * Anfrageformular der Landingpages, jedes eingebettete Calendly-Widget und
 * alles, was sich per data-hide-floating selbst als Sperrflaeche markiert.
 *
 * Hintergrund: Auf 320 bis 390px Breite lagen Pille, Beratungsknopf und die
 * Beratungsleiste ueber dem Formular, der Button "Anfrage absenden" war auf
 * dem iPhone SE nicht mehr direkt antippbar.
 */
const TARGET_SELECTOR = '#anfrage, .calendly-inline-widget, [data-hide-floating]'

/**
 * Liefert true, solange eines der Ziele im Viewport liegt. Die untere
 * rootMargin von -10% laesst die Aktionen noch stehen, solange das Formular
 * gerade erst am unteren Rand auftaucht.
 *
 * Calendly ersetzt das Inline-Widget nachtraeglich und Seiten wechseln per
 * Client-Navigation, deshalb beobachtet ein MutationObserver neu
 * hinzukommende Ziele.
 */
export function useHideNearTargets(): boolean {
  const pathname = usePathname()
  const [hidden, setHidden] = useState(false)

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return

    const visible = new Set<Element>()
    const observed = new Set<Element>()

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target)
          else visible.delete(entry.target)
        }
        setHidden(visible.size > 0)
      },
      { rootMargin: '0px 0px -10% 0px' }
    )

    const scan = () => {
      for (const el of document.querySelectorAll(TARGET_SELECTOR)) {
        if (observed.has(el)) continue
        observed.add(el)
        io.observe(el)
      }
      // Aus dem DOM entfernte Ziele nicht weiter als sichtbar zaehlen
      for (const el of observed) {
        if (el.isConnected) continue
        observed.delete(el)
        visible.delete(el)
        io.unobserve(el)
      }
      setHidden(visible.size > 0)
    }

    scan()
    let frame = 0
    const mo = new MutationObserver(() => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(scan)
    })
    mo.observe(document.body, { childList: true, subtree: true })

    return () => {
      cancelAnimationFrame(frame)
      mo.disconnect()
      io.disconnect()
    }
  }, [pathname])

  return hidden
}

export default useHideNearTargets
